import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Settings, Package, Plus, Save, Trash2, X, Database, Edit2 } from 'lucide-react';
import {
  fetchAllProposalProducts, createProposalProduct, updateProposalProduct,
  deleteProposalProduct, fetchProposalSettings, updateProposalSettings,
  seedProposalCatalog
} from '../actions/proposalCatalog';
import '../styles/ProposalPricesView.css';

// ─── Design Tokens ─────────────────────────────────────────
const TEAL    = '#00b4d8';
const DARK    = '#001d3a';
const BG      = '#f4f6f9';
const CARD    = '#ffffff';
const BORDER  = '#e2e8f0';
const TEXT_1  = '#1e293b';
const TEXT_2  = '#64748b';
const RED     = '#ef4444';
const GREEN   = '#10b981';

const cardStyle   = { background: CARD, borderRadius: 12, border: `1px solid ${BORDER}`, padding: 24, marginBottom: 20 };
const labelStyle  = { fontSize: 12, fontWeight: 600, color: TEXT_2, marginBottom: 4, display: 'block' };
const inputStyle  = { width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 14, color: TEXT_1, outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: '#fff', cursor: 'pointer' };
const btnPrimary  = { background: TEAL, border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 };
const btnDanger   = { ...btnPrimary, background: RED };
const btnOutline  = { ...btnPrimary, background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT_1 };
const sectionTitle = { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: TEXT_2 };

const MAINT_ZONES  = ['ZONA_I', 'ZONA_II', 'ZONA_III', 'ZONA_IV'];
const MAINT_RANGES = ['0-500', '501-1000', '1001-2000'];
const MAINT_ZONE_LABELS = {
  ZONA_I:   'ZONA I',
  ZONA_II:  'ZONA II',
  ZONA_III: 'ZONA III',
  ZONA_IV:  'ZONA IV',
};

const EMPTY_PRODUCT = { category: 'cabinet', code: '', codeIndoor: '', codeOutdoor: '', name: '', lockers: 0, env: 'both', image: '', priceInterior: 0, priceExterior: 0, rentInterior: 0, rentExterior: 0, personalizationPrice: 0, personalizationCost: 0, assetCostInterior: 0, assetCostExterior: 0, widthInterior: '', widthExterior: '', sortOrder: 0 };
const RENTAB_ADMIN_IDS = ['5b03210d8aca3af35664d0e2', '5cf612434cffd40ee39fcc2c'];

const IMAGE_OPTIONS = [
  { value: '',          label: '— Ninguna —',             group: 'all' },
  { value: 'VLK08',     label: 'VLK08 (Kiosco interior)',  group: 'module' },
  { value: 'VLK06',     label: 'VLK06 (Kiosco exterior)',  group: 'module' },
  { value: 'VLM02',     label: 'VLM02 (2 lockers)',        group: 'module' },
  { value: 'VLM04',     label: 'VLM04 (4 lockers)',        group: 'module' },
  { value: 'VLM04XL',   label: 'VLM04XL (4 lockers XL)',   group: 'module' },
  { value: 'VLM06',     label: 'VLM06 (6 lockers)',        group: 'module' },
  { value: 'VLM06XL',   label: 'VLM06XL (6 lockers XL)',   group: 'module' },
  { value: 'VLM10',     label: 'VLM10 (10 lockers)',       group: 'module' },
  { value: 'VLM12',     label: 'VLM12 (12 lockers)',       group: 'module' },
  { value: 'qr',        label: 'QR Panel',                 group: 'module' },
  { value: 'key_pin',   label: 'Llave PIN',                group: 'spare' },
  { value: 'key_rfid',  label: 'Llave RFID',               group: 'spare' },
  { value: 'lock_hook', label: 'Cerradura Gancho',         group: 'spare' },
  { value: 'lock_public', label: 'Cerradura Pública',      group: 'spare' },
  { value: 'lock_slide',  label: 'Cerradura Corredera',    group: 'spare' },
  { value: 'final',       label: 'Final',                  group: 'spare' },
];

// Returns a preview URL for a given image key
const getImagePreviewUrl = (key) => {
  if (!key) return null;
  if (key === 'qr') return '/images/configurator/qr.png';
  // Recambio images are direct PNGs
  if (key.startsWith('key_') || key.startsWith('lock_') || key === 'final') return `/images/configurator/${key}.png`;
  // Module SVGs — prefer interior, fall back to exterior
  const interior = `/images/configurator/interior_${key}.svg`;
  const exterior = `/images/configurator/exterior_${key}.svg`;
  // We'll try interior first and let <img> onError fall back
  return interior;
};

const ImageSelectWithPreview = ({ value, onChange, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <select value={value || ''} onChange={onChange} style={{ ...style, flex: 1 }}>
      {IMAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    {value ? (
      <img
        src={getImagePreviewUrl(value)}
        alt={value}
        onError={(e) => {
          // try exterior SVG as fallback for modules
          if (!e.target.src.includes('exterior_')) {
            e.target.src = `/images/configurator/exterior_${value}.svg`;
          } else {
            e.target.style.display = 'none';
          }
        }}
        style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, border: `1px solid #e2e8f0`, background: '#f8fafc', flexShrink: 0 }}
      />
    ) : <div style={{ width: 40, height: 40, borderRadius: 6, border: `1px dashed #e2e8f0`, background: '#f8fafc', flexShrink: 0 }} />}
  </div>
);

class ProposalPricesView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editingCell: null, // { id, field }
      cellValue: '',
      addingProduct: false,
      newProduct: { ...EMPTY_PRODUCT },
      editSettings: false,
      localSettings: null,
      categoryFilter: 'all',
    };
  }

  componentDidMount() {
    this.props.fetchAllProposalProducts();
    this.props.fetchProposalSettings();
  }

  componentDidUpdate(prevProps) {
    if (this.props.needsRefresh && !prevProps.needsRefresh) {
      this.props.fetchAllProposalProducts();
    }
    if (this.props.settings && !this.state.localSettings) {
      this.setState({ localSettings: this.toPlainSettings(this.props.settings) });
    }
  }

  toPlainSettings(s) {
    const lp = s.licensePricing || {};
    const mp = s.maintenancePricing || {};
    const buildMatrix = (typeData, defaults) => {
      const result = {};
      MAINT_ZONES.forEach((z, zi) => {
        result[z] = {};
        MAINT_RANGES.forEach((r, ri) => {
          // Support both old flat number and new nested object
          const nested = typeData && typeof typeData === 'object' && typeData[z];
          result[z][r] = (nested && nested[r] != null) ? nested[r] : (defaults[zi][ri] ?? 0);
        });
      });
      return result;
    };
    const defaultsEstandar = [
      [1500, 2250, 3000],
      [2500, 3500, 4500],
      [3000, 4250, 5500],
      [3250, 4750, 6000],
    ];
    const defaultsPremium = [
      [3100, 4250, 5750],
      [4750, 6750, 8750],
      [6000, 8500, 11000],
      [6500, 9250, 12000],
    ];
    return {
      licenseProPerPOS:                   lp.PRO?.perPOS    ?? 135,
      licenseBasicPerPOS:                 lp.BASIC?.perPOS  ?? 99,
      licenseLockerStd:                   lp.lockerStd      ?? 50,
      personalizationProjectCost:         s.personalizationProjectCost         ?? 0,
      internalLicenseCostPerLockerYear:   s.internalLicenseCostPerLockerYear   ?? 1.25,
      internalMaintCostPerLockerYear:     s.internalMaintCostPerLockerYear     ?? 3.50,
      maintenanceEstandar: buildMatrix(mp.ESTANDAR, defaultsEstandar),
      maintenancePremium:  buildMatrix(mp.PREMIUM,  defaultsPremium),
    };
  }

  handleSeed = async () => {
    await this.props.seedProposalCatalog();
    this.props.fetchAllProposalProducts();
    this.props.fetchProposalSettings();
  };

  // ─── Product CRUD ────────────────────────────────────────
  startCellEdit = (id, field, value) => {
    this.setState({ editingCell: { id, field }, cellValue: value ?? '' });
  };

  cancelCellEdit = () => {
    this.setState({ editingCell: null, cellValue: '' });
  };

  commitCellEdit = async (productId, field) => {
    const { editingCell, cellValue } = this.state;
    if (!editingCell || editingCell.id !== productId || editingCell.field !== field) return;
    const product = (this.props.products || []).find(p => p._id === productId);
    if (!product) { this.cancelCellEdit(); return; }
    const numFields = ['lockers', 'priceInterior', 'priceExterior', 'rentInterior', 'rentExterior', 'personalizationPrice', 'personalizationCost', 'assetCostInterior', 'assetCostExterior', 'sortOrder'];
    const nullableNumFields = ['widthInterior', 'widthExterior'];
    let finalValue = cellValue;
    if (numFields.includes(field)) {
      finalValue = Number(cellValue) || 0;
    } else if (nullableNumFields.includes(field)) {
      finalValue = (cellValue === '' || cellValue === null || cellValue === undefined) ? null : Number(cellValue);
    }
    this.setState({ editingCell: null, cellValue: '' });
    await this.props.updateProposalProduct(productId, { ...product, [field]: finalValue });
  };

  startAdd = () => {
    this.setState({ addingProduct: true, newProduct: { ...EMPTY_PRODUCT }, editingProduct: null });
  };

  cancelAdd = () => {
    this.setState({ addingProduct: false });
  };

  saveAdd = async () => {
    await this.props.createProposalProduct(this.state.newProduct);
    this.setState({ addingProduct: false, newProduct: { ...EMPTY_PRODUCT } });
  };

  handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este producto? Se ocultará del configurador.')) return;
    await this.props.deleteProposalProduct(id);
  };

  // ─── Settings ────────────────────────────────────────────
  saveSettings = async () => {
    const ls = this.state.localSettings;
    // Ensure all matrix values are numbers
    const numMatrix = (m) => {
      const out = {};
      MAINT_ZONES.forEach(z => {
        out[z] = {};
        MAINT_RANGES.forEach(r => { out[z][r] = Number(m[z][r]) || 0; });
      });
      return out;
    };
    await this.props.updateProposalSettings({
      licensePricing: {
        PRO:      { perPOS: Number(ls.licenseProPerPOS)   },
        BASIC:    { perPOS: Number(ls.licenseBasicPerPOS) },
        lockerStd: Number(ls.licenseLockerStd),
      },
      maintenancePricing: {
        ESTANDAR: numMatrix(ls.maintenanceEstandar),
        PREMIUM:  numMatrix(ls.maintenancePremium),
      },
      personalizationProjectCost:       Number(ls.personalizationProjectCost)       || 0,
      internalLicenseCostPerLockerYear: Number(ls.internalLicenseCostPerLockerYear) || 1.25,
      internalMaintCostPerLockerYear:   Number(ls.internalMaintCostPerLockerYear)   || 3.50,
    });
    this.setState({ editSettings: false });
  };

  // ─── Render helpers ──────────────────────────────────────
  renderProductRow(p) {
    const { editingCell, cellValue } = this.state;
    const isAdmin = RENTAB_ADMIN_IDS.includes(this.props.authId);
    const inactive = !p.isActive;
    const isEditing = (field) => editingCell && editingCell.id === p._id && editingCell.field === field;

    const cellInput = (field, type = 'text', width = 80, extraStyle = {}) => (
      <input
        autoFocus
        type={type}
        value={cellValue}
        onChange={e => this.setState({ cellValue: e.target.value })}
        onBlur={() => this.commitCellEdit(p._id, field)}
        onKeyDown={e => {
          if (e.key === 'Enter') this.commitCellEdit(p._id, field);
          if (e.key === 'Escape') this.cancelCellEdit();
        }}
        style={{ ...inputStyle, width, textAlign: type === 'number' ? 'right' : 'left', ...extraStyle }}
      />
    );

    const cellSelect = (field, options, width = 100) => (
      <select
        autoFocus
        value={cellValue}
        onChange={e => this.setState({ cellValue: e.target.value }, () => this.commitCellEdit(p._id, field))}
        style={{ ...selectStyle, width }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );

    const click = (field, raw) => ({
      onClick: () => this.startCellEdit(p._id, field, raw),
      title: 'Clic para editar',
    });

    return (
      <tr key={p._id} style={{ background: inactive ? '#fef2f2' : 'transparent', opacity: inactive ? 0.5 : 1 }}>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('category', p.category)}>
          {isEditing('category')
            ? cellSelect('category', [{ value: 'cabinet', label: 'Armario' }, { value: 'pos', label: 'Punto Venta' }, { value: 'recambio', label: 'Recambio' }])
            : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4,
                background: p.category === 'cabinet' ? '#e0f2fe' : p.category === 'pos' ? '#fef3c7' : '#ede9fe',
                color:      p.category === 'cabinet' ? '#0369a1' : p.category === 'pos' ? '#92400e' : '#6d28d9',
                fontWeight: 600 }}>
                {p.category === 'cabinet' ? 'Armario' : p.category === 'pos' ? 'P.Venta' : 'Recambio'}
              </span>
          }
        </td>
        <td style={{ ...tdS, fontWeight: 600, cursor: 'pointer' }} {...click('code', p.code)}>
          {isEditing('code') ? cellInput('code', 'text', 80) : p.code}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('codeIndoor', p.codeIndoor || '')}>
          {isEditing('codeIndoor') ? cellInput('codeIndoor', 'text', 90) : <span style={{ fontSize: 11, color: '#0369a1' }}>{p.codeIndoor || '—'}</span>}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('codeOutdoor', p.codeOutdoor || '')}>
          {isEditing('codeOutdoor') ? cellInput('codeOutdoor', 'text', 90) : <span style={{ fontSize: 11, color: '#92400e' }}>{p.codeOutdoor || '—'}</span>}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('name', p.name)}>
          {isEditing('name') ? cellInput('name', 'text', 200) : p.name}
        </td>
        <td style={{ ...tdS, textAlign: 'center', cursor: 'pointer' }} {...click('lockers', p.lockers)}>
          {isEditing('lockers') ? cellInput('lockers', 'number', 50) : p.lockers}
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('env', p.env || 'both')}>
          {isEditing('env')
            ? cellSelect('env', [{ value: 'indoor', label: 'Interior' }, { value: 'outdoor', label: 'Exterior' }, { value: 'both', label: 'Ambos' }], 90)
            : <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: p.env === 'indoor' ? '#dbeafe' : p.env === 'outdoor' ? '#fef3c7' : '#e2e8f0', color: p.env === 'indoor' ? '#1e40af' : p.env === 'outdoor' ? '#92400e' : '#475569', fontWeight: 600 }}>{p.env === 'indoor' ? 'Interior' : p.env === 'outdoor' ? 'Exterior' : 'Ambos'}</span>
          }
        </td>
        <td style={{ ...tdS, cursor: 'pointer' }} {...click('image', p.image || '')}>
          {isEditing('image')
            ? <ImageSelectWithPreview value={cellValue} onChange={e => this.setState({ cellValue: e.target.value }, () => this.commitCellEdit(p._id, 'image'))} style={{ ...selectStyle, width: 140 }} />
            : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {p.image && <img src={getImagePreviewUrl(p.image)} alt={p.image} onError={(e) => { if (!e.target.src.includes('exterior_')) { e.target.src = `/images/configurator/exterior_${p.image}.svg`; } else { e.target.style.display='none'; } }} style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 4, border: `1px solid #e2e8f0`, background: '#f8fafc' }} />}
                <span style={{ fontSize: 11 }}>{p.image || '—'}</span>
              </div>
            )
          }
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('personalizationCost', p.personalizationCost || 0)}>
            {isEditing('personalizationCost') ? cellInput('personalizationCost', 'number', 70, { background: '#fffbeb' }) : `${(p.personalizationCost || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('personalizationPrice', p.personalizationPrice || 0)}>
          {isEditing('personalizationPrice') ? cellInput('personalizationPrice', 'number', 70) : `${p.personalizationPrice || 0} €`}
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('assetCostInterior', p.assetCostInterior || 0)}>
            {isEditing('assetCostInterior') ? cellInput('assetCostInterior', 'number', 80, { background: '#fffbeb' }) : `${(p.assetCostInterior || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('priceInterior', p.priceInterior)}>
          {isEditing('priceInterior') ? cellInput('priceInterior', 'number', 80) : `${p.priceInterior.toLocaleString()} €`}
        </td>
        {isAdmin && (
          <td style={{ ...tdS, textAlign: 'right', color: '#92400e', background: '#fffbeb', fontWeight: 600, cursor: 'pointer' }} {...click('assetCostExterior', p.assetCostExterior || 0)}>
            {isEditing('assetCostExterior') ? cellInput('assetCostExterior', 'number', 80, { background: '#fffbeb' }) : `${(p.assetCostExterior || 0).toLocaleString()} €`}
          </td>
        )}
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer' }} {...click('priceExterior', p.priceExterior)}>
          {isEditing('priceExterior') ? cellInput('priceExterior', 'number', 80) : `${p.priceExterior.toLocaleString()} €`}
        </td>
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer', color: p.widthInterior != null ? TEXT_1 : TEXT_2 }} {...click('widthInterior', p.widthInterior ?? '')}>
          {isEditing('widthInterior') ? cellInput('widthInterior', 'number', 70) : p.widthInterior != null ? `${p.widthInterior} cm` : 'auto'}
        </td>
        <td style={{ ...tdS, textAlign: 'right', cursor: 'pointer', color: p.widthExterior != null ? TEXT_1 : TEXT_2 }} {...click('widthExterior', p.widthExterior ?? '')}>
          {isEditing('widthExterior') ? cellInput('widthExterior', 'number', 70) : p.widthExterior != null ? `${p.widthExterior} cm` : 'auto'}
        </td>
        <td style={{ ...tdS, textAlign: 'center', cursor: 'pointer' }} {...click('sortOrder', p.sortOrder)}>
          {isEditing('sortOrder') ? cellInput('sortOrder', 'number', 50) : p.sortOrder}
        </td>
        <td style={tdS}>
          {p.isActive && (
            <button onClick={() => this.handleDelete(p._id)} style={{ ...btnDanger, padding: '5px 8px', fontSize: 11 }}><Trash2 size={12} /></button>
          )}
        </td>
      </tr>
    );
  }

  renderNewRow() { return null; } // replaced by modal

  renderAddModal() {
    const { addingProduct, newProduct } = this.state;
    if (!addingProduct) return null;

    const np  = newProduct;
    const isAdmin = RENTAB_ADMIN_IDS.includes(this.props.authId);
    const set  = (field) => (e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, [field]: e.target.value } }));
    const setN = (field) => (e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, [field]: Number(e.target.value) || 0 } }));

    const field = (lbl, children) => (
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>{lbl}</label>
        {children}
      </div>
    );

    return (
      <div
        onClick={(e) => e.target === e.currentTarget && this.cancelAdd()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,10,30,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '20px 24px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#001d3a' }}>Nuevo Producto</div>
            <button onClick={this.cancelAdd} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: TEXT_2, padding: 4, display: 'flex', alignItems: 'center' }}><X size={20} /></button>
          </div>

          {/* Body */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>

              {field('Tipo', <select value={np.category} onChange={set('category')} style={selectStyle}><option value="cabinet">Armario</option><option value="pos">Punto de Venta</option><option value="recambio">Recambio</option></select>)}
              {field('Código', <input value={np.code} onChange={set('code')} placeholder="LK12" style={inputStyle} />)}
              {field('Código Interior', <input value={np.codeIndoor || ''} onChange={set('codeIndoor')} placeholder="LK12-INT" style={inputStyle} />)}
              {field('Código Exterior', <input value={np.codeOutdoor || ''} onChange={set('codeOutdoor')} placeholder="LK12-EXT" style={inputStyle} />)}

              <div style={{ gridColumn: '1 / -1', marginBottom: 14 }}>
                <label style={labelStyle}>Nombre</label>
                <input value={np.name} onChange={set('name')} placeholder="Nombre del producto" style={inputStyle} />
              </div>

              {field('Taquillas', <input type="number" value={np.lockers} onChange={setN('lockers')} style={inputStyle} />)}
              {field('Entorno', <select value={np.env || 'both'} onChange={set('env')} style={selectStyle}><option value="indoor">Interior</option><option value="outdoor">Exterior</option><option value="both">Ambos</option></select>)}

              <div style={{ gridColumn: '1 / -1', marginBottom: 14 }}>
                <label style={labelStyle}>Imagen</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px', background: '#f8fafc', borderRadius: 8, border: `1px solid ${BORDER}` }}>
                  {IMAGE_OPTIONS.filter(o => o.value !== '').map(o => {
                    const selected = np.image === o.value;
                    const src = getImagePreviewUrl(o.value);
                    return (
                      <div key={o.value} onClick={() => this.setState(prev => ({ newProduct: { ...prev.newProduct, image: o.value } }))}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${selected ? TEAL : 'transparent'}`, background: selected ? '#e0f9ff' : '#fff', minWidth: 64, transition: 'all 0.1s' }}>
                        <img src={src} alt={o.label}
                          onError={(e) => { if (!e.target.src.includes('exterior_')) { e.target.src = `/images/configurator/exterior_${o.value}.svg`; } else { e.target.style.opacity = '0.3'; } }}
                          style={{ width: 48, height: 48, objectFit: 'contain' }} />
                        <span style={{ fontSize: 10, color: selected ? TEAL : TEXT_2, fontWeight: selected ? 700 : 400, textAlign: 'center', maxWidth: 64, lineHeight: 1.2 }}>{o.label}</span>
                      </div>
                    );
                  })}
                  <div onClick={() => this.setState(prev => ({ newProduct: { ...prev.newProduct, image: '' } }))}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 6px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${np.image === '' ? TEAL : 'transparent'}`, background: np.image === '' ? '#e0f9ff' : '#fff', minWidth: 64 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 6, border: `1px dashed #cbd5e1`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={18} color="#94a3b8" />
                    </div>
                    <span style={{ fontSize: 10, color: np.image === '' ? TEAL : TEXT_2, fontWeight: np.image === '' ? 700 : 400, textAlign: 'center' }}>Ninguna</span>
                  </div>
                </div>
              </div>

              {field('Precio Interior (€)', <input type="number" value={np.priceInterior} onChange={setN('priceInterior')} style={inputStyle} />)}
              {field('Precio Exterior (€)', <input type="number" value={np.priceExterior} onChange={setN('priceExterior')} style={inputStyle} />)}
              {field('Personalización (€)', <input type="number" value={np.personalizationPrice || 0} onChange={setN('personalizationPrice')} style={inputStyle} />)}
              {field('Orden', <input type="number" value={np.sortOrder} onChange={setN('sortOrder')} style={inputStyle} />)}
              {field('Ancho Int. (cm)', <input type="number" step="0.1" value={np.widthInterior ?? ''} onChange={(e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, widthInterior: e.target.value === '' ? null : Number(e.target.value) } }))} placeholder="auto" style={inputStyle} />)}
              {field('Ancho Ext. (cm)', <input type="number" step="0.1" value={np.widthExterior ?? ''} onChange={(e) => this.setState(prev => ({ newProduct: { ...prev.newProduct, widthExterior: e.target.value === '' ? null : Number(e.target.value) } }))} placeholder="auto" style={inputStyle} />)}

              {isAdmin && field('Coste Pers. (€)', <input type="number" value={np.personalizationCost || 0} onChange={setN('personalizationCost')} style={{ ...inputStyle, background: '#fffbeb' }} />)}
              {isAdmin && field('Coste Asset Int. (€)', <input type="number" value={np.assetCostInterior || 0} onChange={setN('assetCostInterior')} style={{ ...inputStyle, background: '#fffbeb' }} />)}
              {isAdmin && field('Coste Asset Ext. (€)', <input type="number" value={np.assetCostExterior || 0} onChange={setN('assetCostExterior')} style={{ ...inputStyle, background: '#fffbeb' }} />)}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 24px', borderTop: `1px solid ${BORDER}`, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={this.cancelAdd} style={{ ...btnOutline, padding: '9px 20px' }}><X size={13} /> Cancelar</button>
            <button onClick={this.saveAdd} style={{ ...btnPrimary, padding: '9px 20px' }}><Save size={13} /> Guardar producto</button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { products, loading } = this.props;
    const { editSettings, localSettings, categoryFilter } = this.state;
    const hasProducts = products && products.length > 0;
    const filteredProducts = categoryFilter === 'all'
      ? (products || [])
      : (products || []).filter(p => p.category === categoryFilter);

    return (
      <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: BG, minHeight: '100vh', padding: '24px 24px 60px' }}>
        {/* Header */}
        <div className="pprice-header">
          <div className="pprice-header-content">
            <div className="pprice-header-text">
              <h1>
                <Settings size={28} className="pprice-header-icon" />
                Catálogo de Precios
              </h1>
              <span className="pprice-subtitle">Gestiona productos y precios del configurador de propuestas</span>
            </div>
            <div className="pprice-header-actions">
              {!hasProducts && (
                <button onClick={this.handleSeed} disabled={loading} className="pprice-header-btn primary">
                  <Database size={14} /> Inicializar catálogo
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          {/* Settings Card */}
          {localSettings && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={sectionTitle}>Tarifas Generales</div>
                {!editSettings ? (
                  <button onClick={() => this.setState({ editSettings: true })} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12 }}><Edit2 size={12} /> Editar</button>
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={this.saveSettings} style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}><Save size={12} /> Guardar</button>
                    <button onClick={() => this.setState({ editSettings: false, localSettings: this.toPlainSettings(this.props.settings) })} style={{ ...btnOutline, padding: '6px 12px', fontSize: 12 }}><X size={12} /></button>
                  </div>
                )}
              </div>
              {/* License prices + Maintenance matrices in columns */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'start' }}>
                {/* License prices */}
                <div style={{ flex: '1 1 260px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_2, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Licencias</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[['BÁSICA (€/PdV/mes)', 'licenseBasicPerPOS'], ['PRO (€/PdV/mes)', 'licenseProPerPOS'], ['Baterías (€/ud)', 'licenseLockerStd'], ['Personalización (€/proyecto)', 'personalizationProjectCost'], ['Coste lic. interno (€/taq/año)', 'internalLicenseCostPerLockerYear'], ['Coste mant. interno (€/taq/año)', 'internalMaintCostPerLockerYear']].map(([lbl, key]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <label style={{ ...labelStyle, marginBottom: 0, minWidth: 130, whiteSpace: 'nowrap' }}>{lbl}</label>
                        {editSettings ? (
                          <input type="number" step="0.01" value={localSettings[key]}
                            onChange={(e) => this.setState(prev => ({ localSettings: { ...prev.localSettings, [key]: e.target.value } }))}
                            style={{ ...inputStyle, width: 90, textAlign: 'right' }} />
                        ) : (
                          <span style={{ fontSize: 15, fontWeight: 700, color: TEXT_1 }}>{Number(localSettings[key]).toFixed(2)} €</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Maintenance matrices */}
                {[['ESTANDAR', 'maintenanceEstandar', 'Mantenimiento ESTÁNDAR'], ['PREMIUM', 'maintenancePremium', 'Mantenimiento PREMIUM']].map(([, matKey, title]) => (
                  <div key={matKey} style={{ flex: '1 1 400px' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_2, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title} (€/año)</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={thCellS}>Taquillas</th>
                            {MAINT_ZONES.map(z => <th key={z} style={thCellS}>{MAINT_ZONE_LABELS[z]}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {MAINT_RANGES.map(range => (
                            <tr key={range}>
                              <td style={{ ...tdCellS, fontWeight: 600, color: TEXT_2, textAlign: 'left' }}>{range}</td>
                              {MAINT_ZONES.map(zone => (
                                <td key={zone} style={tdCellS}>
                                  {editSettings ? (
                                    <input type="number" value={localSettings[matKey][zone][range]}
                                      onChange={(e) => this.setState(prev => ({
                                        localSettings: {
                                          ...prev.localSettings,
                                          [matKey]: {
                                            ...prev.localSettings[matKey],
                                            [zone]: { ...prev.localSettings[matKey][zone], [range]: e.target.value }
                                          }
                                        }
                                      }))}
                                      style={{ ...inputStyle, width: 90, textAlign: 'right', padding: '4px 8px' }} />
                                  ) : (
                                    <span style={{ fontWeight: 600 }}>{Number(localSettings[matKey][zone][range]).toLocaleString('es-ES')} €</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products Table */}
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px 12px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={16} color={TEAL} />
                <span style={sectionTitle}>Productos</span>
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: TEXT_2, fontWeight: 600, marginRight: 2 }}>Tipo:</span>
                {[['all','Todos'], ['cabinet','Armarios'], ['pos','P. Venta'], ['recambio','Recambios']].map(([val, lbl]) => (
                  <button key={val} onClick={() => this.setState({ categoryFilter: val })}
                    style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                      background: categoryFilter === val ? TEAL : '#f1f5f9',
                      color: categoryFilter === val ? '#fff' : TEXT_2 }}>
                    {lbl}
                  </button>
                ))}
              </div>
              <button onClick={this.startAdd} style={{ ...btnPrimary, padding: '7px 14px', fontSize: 12 }}><Plus size={14} /> Nuevo producto</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                <thead>
                  <tr>
                    {['Tipo', 'Código', 'Cód. Int.', 'Cód. Ext.', 'Nombre', 'Lockers', 'Entorno', 'Imagen'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Pers. €</th>}
                    <th style={thS}>Pers. €</th>
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Int. €</th>}
                    <th style={thS}>Precio Int.</th>
                    {RENTAB_ADMIN_IDS.includes(this.props.authId) && <th style={{ ...thS, color: '#92400e', background: '#fffbeb' }}>Coste Ext. €</th>}
                    <th style={thS}>Precio Ext.</th>
                    {['Ancho Int. (cm)', 'Ancho Ext. (cm)', 'Orden', 'Acciones'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(p => this.renderProductRow(p))}
                  {!loading && hasProducts === false && (
                    <tr><td colSpan={12} style={{ padding: 40, textAlign: 'center', color: TEXT_2 }}>
                      No hay productos. Usa "Inicializar catálogo" para cargar valores por defecto.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {this.renderAddModal()}
      </div>
    );
  }
}

const thS = { padding: '10px 8px', textAlign: 'left', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0e7490', background: '#f0fdfa', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' };
const tdS = { padding: '8px 8px', borderBottom: '1px solid #f1f5f9', fontSize: 13, color: '#1e293b', verticalAlign: 'middle' };
const thCellS = { padding: '7px 12px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#0e7490', background: '#f0fdfa', border: '1px solid #e2e8f0', whiteSpace: 'nowrap', textAlign: 'center' };
const tdCellS = { padding: '6px 12px', border: '1px solid #e2e8f0', fontSize: 13, color: '#1e293b', textAlign: 'right' };

const mapStateToProps = (state) => ({
  products: state.proposalCatalog.products,
  settings: state.proposalCatalog.settings,
  needsRefresh: state.proposalCatalog.needsRefresh,
  loading: state.proposalCatalog.loading,
  error: state.proposalCatalog.error,
  role: state.auth.role,
  type: state.auth.type,
  authId: state.auth.id,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchAllProposalProducts, createProposalProduct, updateProposalProduct,
  deleteProposalProduct, fetchProposalSettings, updateProposalSettings,
  seedProposalCatalog,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProposalPricesView);
